import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Package,
  MapPin,
  Scan,
  Plus,
  CheckCircle,
  AlertCircle,
  Loader,
  Save,
  RotateCcw,
  Edit2,
  Trash2,
  X,
  Check,
  Search,
  Settings,
  Lock,
  Unlock,
  Hash,
  ClipboardList,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  inventoryService,
  InventorySession,
  InventoryCountLine,
  InventoryFormData,
  ItemLocationInfo,
  LocationItem
} from '../../../../services/inventoryService';
import { ItemSearchModal, LocationInfo } from '../../../common/ItemSearchModal';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { ItemsDto } from '../../../../types/items';
import { SettingsModal } from './SettingsModal';
import { SerialCountingModal, SerialCountResult } from './modals/SerialCountingModal';
import { BatchCountingModal, BatchCountResult } from './modals/BatchCountingModal';
import { LocationZeroCountDialog } from './modals/LocationZeroCountDialog';
import { useProfileBoolSetting } from '../../../../hooks/useProfileBoolSetting';
import { userProfileService } from '../../../../services/userProfileService';
import { systemSetupService } from '../../../../services/systemSetupService';
import { useItemImageConfig } from '../../../../hooks/useItemImageConfig';
import { ImageModal } from '../picking/modals/ImageModal';
import { parseGS1Barcode, GS1Data } from '../../../../utils/gs1Parser';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput, MobileBarcodeCaptureInputHandle } from '../../../common/scanning';
import { NumericInput } from '../../../common/inputs';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { CountingListSelector } from './CountingListSelector';
import { CountingListCounting } from './CountingListCounting';
import { CountingListOverviewItem } from '../../../../types/countingListCreation';
import { CapacityUpdateModal } from '../../../shared/CapacityUpdateModal';
import { Layers } from 'lucide-react';
import { stcBinService } from '../../../../services/stcBinService';
import { itemBinSizeService } from '../../../../services/itemBinSizeService';

interface InventoryAppProps {
  onBack: () => void;
}

interface EditingLine {
  lineID: number;
  quantity: string;
}

type CountingMode = 'free-counting' | 'counting-list';

/** Talt vare på aktiv lokasjon (TW 36413029). */
interface CountedLocationItem {
  itemNo: string;
  description: string;
  countedQuantity: number;
  /** Saldo da lista ble hentet; undefined for varer som ikke sto på lista. */
  balance?: number;
  isNew: boolean;
}

const sameCode = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

// TW 36413029: «Vis varer på lokasjon» er en GUI-innstilling, ikke en systemparameter (beslutning
// Lars 2026-10-07). Huskes i nettleseren per tenant + operatør, som hurtigmodus i telleliste-fanen.
const showItemsOnLocationStorageKey = (customerNo?: string, operatorId?: string) =>
  `inventory.showItemsOnLocation.${customerNo ?? ''}.${operatorId ?? ''}`;
// Om lokasjonskortet er kollapset — også en GUI-preferanse per tenant + operatør
const locationCardCollapsedStorageKey = (customerNo?: string, operatorId?: string) =>
  `inventory.locationItemsCollapsed.${customerNo ?? ''}.${operatorId ?? ''}`;

export const InventoryApp: React.FC<InventoryAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse, warehouses } = useWarehouse();
  // Lageret en ny telling opprettes på: valgt lager, eller det eneste lageret når tenanten bare har ett
  // (lagervelgeren viser da bare et merke, og currentWarehouse er ofte null)
  const countingWarehouseId = currentWarehouse || (warehouses.length === 1 ? warehouses[0].warehouseID : null);
  const activeWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse);
  const isAdvancedWarehouse = activeWarehouse?.useAWYesNo ?? false;
  // TW 36386710 (vedtatt 2026-09-22): lokasjon er valgfri i fritelling når verken Compass
  // (UseAWYesNo=0) eller ERP (ERPAWYesNo=0) sporer lokasjon — Compass GO. Da flyter operatøren
  // «skann vare → antall → OK», fast lokasjon vises som tekst og brukes som standard på linja,
  // og alle tellinger summeres per vare (regel i 36386709). Ingen egen innstilling.
  const locationOptional = !!activeWarehouse && !activeWarehouse.useAWYesNo && !activeWarehouse.erpAWYesNo;

  // Counting mode: free counting vs predefined counting list
  const [countingMode, setCountingMode] = useState<CountingMode>('free-counting');
  const [selectedCountingList, setSelectedCountingList] = useState<CountingListOverviewItem | null>(null);

  const [session, setSession] = useState<InventorySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false); // Separate loading for session operations
  // «Alle lager» valgt og ingen aktiv telling: en telling må gjelde ett lager
  const [warehouseRequired, setWarehouseRequired] = useState(false);
  const [formData, setFormData] = useState<InventoryFormData>({
    scannedCode: '',
    quantity: '',
    isScanning: false,
    validationMessage: '',
    validationType: ''
  });
  const [currentItem, setCurrentItem] = useState<{ itemNo: string; description: string; itemType?: number; } | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ locationName: string; } | null>(null);
  // GO-fritelling: foreslått fast lokasjon for gjeldende vare (kun informasjon + standardverdi)
  const [suggestedLocation, setSuggestedLocation] = useState<string | null>(null);
  // Varebilde (TW 36391639): samme innstillinger og profilflagg som plukk, navn fra Items.ItemPictureName
  const { buildItemImageUrl } = useItemImageConfig();
  const [showItemPicture, setShowItemPicture] = useState<boolean | null>(null);
  const [currentItemImageUrl, setCurrentItemImageUrl] = useState<string | null>(null);
  const [itemImageFailed, setItemImageFailed] = useState(false);
  const [showItemImageModal, setShowItemImageModal] = useState(false);
  useEffect(() => {
    let cancelled = false;
    userProfileService.getCurrentUserProfile()
      .then(p => { if (!cancelled) setShowItemPicture(p?.showItemPicureYesNo ?? null); })
      .catch(() => { /* profil ikke tilgjengelig → standard (vis) */ });
    return () => { cancelled = true; };
  }, []);

  // «Maks antall per linje» (SystemSetup.MaxLineItemQuantity): advarsel ved høyere antall, som i tellelisten.
  // 0/tom = av. Kun advarsel — operatøren kan bekrefte og lagre likevel.
  const [maxLineItemQuantity, setMaxLineItemQuantity] = useState<number | null>(null);
  const [highQuantityWarning, setHighQuantityWarning] = useState<{ source: 'add' | 'edit'; quantity: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    systemSetupService.getCurrent()
      .then(setup => {
        if (!cancelled && setup.maxLineItemQuantity != null && setup.maxLineItemQuantity > 0) {
          setMaxLineItemQuantity(setup.maxLineItemQuantity);
        }
      })
      .catch(err => console.warn('Kunne ikke hente MaxLineItemQuantity fra systeminnstillinger:', err));
    return () => { cancelled = true; };
  }, []);
  const setItemImageFor = (pictureName?: string | null) => {
    setItemImageFailed(false);
    setCurrentItemImageUrl(buildItemImageUrl(pictureName, showItemPicture) ?? null);
  };
  const [showSerialCountingModal, setShowSerialCountingModal] = useState(false);
  const [showBatchCountingModal, setShowBatchCountingModal] = useState(false);
  // Non-intrusive capacity update: track last counted line, operator clicks button to open modal
  const [lastCountedInfo, setLastCountedInfo] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    quantity: number;
  } | null>(null);
  const [capacityModalData, setCapacityModalData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    binSizeNo: number;
    currentQuantity: number;
    currentMaxQuantity?: number;
    existingItemBinSizeId?: number | null;
    existingPutAwayPriorityNo?: number;
  } | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingLine, setEditingLine] = useState<EditingLine | null>(null);
  const [deletingLineId, setDeletingLineId] = useState<number | null>(null);
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState<string>('');

  // Rapid scanning settings — huskes per operatør (TW 36413027)
  const rapidScanningSetting = useProfileBoolSetting('inventory.lock_location', false);
  const enableRapidScanning = rapidScanningSetting.value;
  // «Vis varer på lokasjon» — huskes per operatør i nettleseren, kun når lokasjon er påkrevd (TW 36413029)
  const showItemsStorageKey = showItemsOnLocationStorageKey(user?.customerNo, user?.operatorId);
  const [showItemsOnLocation, setShowItemsOnLocation] = useState<boolean>(() => {
    try { return localStorage.getItem(showItemsStorageKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { setShowItemsOnLocation(localStorage.getItem(showItemsStorageKey) === '1'); } catch { /* localStorage utilgjengelig */ }
  }, [showItemsStorageKey]);
  const itemsOnLocationMode = showItemsOnLocation && !locationOptional;
  const locationCardCollapsedKey = locationCardCollapsedStorageKey(user?.customerNo, user?.operatorId);
  const [locationCardCollapsed, setLocationCardCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(locationCardCollapsedKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { setLocationCardCollapsed(localStorage.getItem(locationCardCollapsedKey) === '1'); } catch { /* localStorage utilgjengelig */ }
  }, [locationCardCollapsedKey]);
  const toggleLocationCardCollapsed = () => {
    setLocationCardCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(locationCardCollapsedKey, next ? '1' : '0'); } catch { /* localStorage utilgjengelig */ }
      return next;
    });
  };
  const [settingsSaveError, setSettingsSaveError] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [lockedLocation, setLockedLocation] = useState<{ locationName: string; } | null>(null);

  // State for search modals
  const [showItemSearchModal, setShowItemSearchModal] = useState(false);
  const [showLocationSearchModal, setShowLocationSearchModal] = useState(false);
  
  const scanInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const editQuantityInputRef = useRef<HTMLInputElement>(null);
  const mobileCaptureRef = useRef<MobileBarcodeCaptureInputHandle>(null);
  const sessionInitialized = useRef(false); // Prevent multiple initialization

  // ── TW 36413029: «Vis varer på lokasjon» ─────────────────────────────────────
  // Aktiv lokasjon: låst lokasjon i hurtigskanning, ellers skannet lokasjon.
  const activeLocationName: string | null =
    (enableRapidScanning && isLocationLocked && lockedLocation)
      ? lockedLocation.locationName
      : (currentLocation?.locationName ?? null);
  const [locationItems, setLocationItems] = useState<LocationItem[] | null>(null);
  const [locationItemsFor, setLocationItemsFor] = useState<string | null>(null);
  const [locationItemsLoading, setLocationItemsLoading] = useState(false);
  const [locationItemsError, setLocationItemsError] = useState<string | null>(null);
  const [locationItemsReloadKey, setLocationItemsReloadKey] = useState(0);
  const [showCountedSection, setShowCountedSection] = useState(false);
  const [zeroDialog, setZeroDialog] = useState<{
    locationName: string;
    items: LocationItem[];
    /** Lokasjonen som ble skannet og skal bli aktiv når dialogen lukkes; null = «Ferdig med lokasjon». */
    nextLocation: string | null;
  } | null>(null);
  const locationItemsRequestRef = useRef(0);

  const loadLocationItems = useCallback(async (binCode: string): Promise<LocationItem[] | null> => {
    const warehouseId = session?.warehouse || currentWarehouse;
    if (!warehouseId) return null;
    const requestId = ++locationItemsRequestRef.current;
    setLocationItemsLoading(true);
    setLocationItemsError(null);
    try {
      const items = await inventoryService.getItemsOnLocation(binCode, warehouseId);
      if (requestId === locationItemsRequestRef.current) {
        setLocationItems(items);
        setLocationItemsFor(binCode);
      }
      return items;
    } catch (error) {
      console.error('Feil ved henting av varer på lokasjon:', error);
      if (requestId === locationItemsRequestRef.current) {
        setLocationItemsError(t('warehouse.modules.inventory.locationItems.loadFailed', {}, 'Kunne ikke hente varene på lokasjonen'));
      }
      return null;
    } finally {
      if (requestId === locationItemsRequestRef.current) setLocationItemsLoading(false);
    }
  }, [session?.warehouse, currentWarehouse, t]);

  // Hent lista når aktiv lokasjon endres, ved hvert lokasjonsskann (reloadKey) og når innstillingen slås på
  useEffect(() => {
    if (!itemsOnLocationMode || !activeLocationName) {
      locationItemsRequestRef.current++;
      setLocationItems(null);
      setLocationItemsFor(null);
      setLocationItemsLoading(false);
      setLocationItemsError(null);
      return;
    }
    loadLocationItems(activeLocationName);
  }, [itemsOnLocationMode, activeLocationName, locationItemsReloadKey, loadLocationItems]);

  // Tellelinjer i sesjonen for en lokasjon, per vare. Nyeste linje per vare (+ batch/serie) teller,
  // som «siste telling vinner» ved fullføring; batch/serie summeres over batchene/seriene.
  const countedByItemAt = useCallback((locationName: string): Map<string, { itemNo: string; description: string; quantity: number }> => {
    const result = new Map<string, { itemNo: string; description: string; quantity: number }>();
    const seen = new Set<string>();
    for (const line of session?.lines ?? []) { // nyeste først
      if (!sameCode(line.locationName, locationName)) continue;
      const itemKey = (line.itemNo ?? '').toLowerCase();
      const lineKey = `${itemKey}|${(line.itemBatchNo || '').toLowerCase()}|${(line.itemSerialNo || '').toLowerCase()}`;
      if (seen.has(lineKey)) continue;
      seen.add(lineKey);
      const entry = result.get(itemKey) ?? { itemNo: line.itemNo, description: line.description1 || '', quantity: 0 };
      entry.quantity += Number(line.quantity) || 0;
      result.set(itemKey, entry);
    }
    return result;
  }, [session?.lines]);

  const uncountedItemsAt = useCallback((locationName: string, items: LocationItem[] | null): LocationItem[] => {
    const counted = countedByItemAt(locationName);
    return (items ?? []).filter(i => !counted.has(i.itemNo.toLowerCase()));
  }, [countedByItemAt]);

  const listIsForActive = !!activeLocationName && sameCode(locationItemsFor, activeLocationName);

  const uncountedLocationItems = useMemo(
    () => (itemsOnLocationMode && activeLocationName && listIsForActive ? uncountedItemsAt(activeLocationName, locationItems) : []),
    [itemsOnLocationMode, activeLocationName, listIsForActive, locationItems, uncountedItemsAt]
  );

  const countedLocationItems = useMemo((): CountedLocationItem[] => {
    if (!itemsOnLocationMode || !activeLocationName) return [];
    const counted = countedByItemAt(activeLocationName);
    const listed = listIsForActive ? (locationItems ?? []) : [];
    const result: CountedLocationItem[] = [];
    for (const item of listed) {
      const c = counted.get(item.itemNo.toLowerCase());
      if (c) result.push({ itemNo: item.itemNo, description: item.description, countedQuantity: c.quantity, balance: item.quantity, isNew: false });
    }
    const listedKeys = new Set(listed.map(i => i.itemNo.toLowerCase()));
    counted.forEach((c, key) => {
      if (!listedKeys.has(key)) result.push({ itemNo: c.itemNo, description: c.description, countedQuantity: c.quantity, isNew: true });
    });
    return result;
  }, [itemsOnLocationMode, activeLocationName, listIsForActive, locationItems, countedByItemAt]);

  // Framdrift på aktiv lokasjon: talt av varene på lista, pluss telt til 0 og nye
  const locationProgress = useMemo(() => {
    const listed = countedLocationItems.filter(i => !i.isNew);
    return {
      total: listIsForActive ? (locationItems?.length ?? 0) : 0,
      counted: listed.length,
      zeroed: listed.filter(i => i.countedQuantity === 0).length,
      newItems: countedLocationItems.length - listed.length
    };
  }, [countedLocationItems, listIsForActive, locationItems]);

  // Gjør en lokasjon aktiv (låst lokasjon følger med i hurtigskanning) og hent lista på nytt
  const activateLocation = (locationName: string) => {
    setCurrentLocation({ locationName });
    if (enableRapidScanning && isLocationLocked) {
      setLockedLocation({ locationName });
    }
    setLocationItemsReloadKey(k => k + 1);
  };

  // Ingen aktiv lokasjon — operatøren skanner neste
  const clearActiveLocation = () => {
    setCurrentLocation(null);
    setIsLocationLocked(false);
    setLockedLocation(null);
    setCurrentItem(null);
    setCurrentItemImageUrl(null);
  };

  /**
   * Lokasjonsbytte eller «Ferdig med lokasjon» (nextLocation = null). Har den aktive lokasjonen
   * utalte varer, åpnes dialogen som går gjennom dem én og én; ellers går operatøren rett videre.
   */
  const leaveActiveLocation = async (nextLocation: string | null) => {
    const current = activeLocationName;
    if (current && (nextLocation === null || !sameCode(current, nextLocation))) {
      const fresh = await loadLocationItems(current);
      const pending = uncountedItemsAt(current, fresh ?? (sameCode(locationItemsFor, current) ? locationItems : null));
      if (pending.length > 0) {
        setZeroDialog({ locationName: current, items: pending, nextLocation });
        setFormData((prev: InventoryFormData) => ({ ...prev, isScanning: false, validationMessage: '', validationType: '' }));
        return;
      }
    }
    proceedFromLocation(current, nextLocation);
  };

  const proceedFromLocation = (previous: string | null, nextLocation: string | null) => {
    if (nextLocation) {
      if (!previous || !sameCode(previous, nextLocation)) {
        setCurrentItem(null);
        setCurrentItemImageUrl(null);
      }
      activateLocation(nextLocation);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.messages.locationFound', { '0': nextLocation }),
        validationType: 'success'
      }));
    } else {
      clearActiveLocation();
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: previous
          ? t('warehouse.modules.inventory.locationItems.locationDone', { '0': previous }, 'Ferdig med {0}. Skann neste lokasjon.')
          : '',
        validationType: previous ? 'success' : ''
      }));
    }
  };

  // «Tell til 0»: én 0-linje for standardvare, én per forventet batch/serie (som «mangler» i modalene)
  const handleZeroCountItem = async (item: LocationItem) => {
    if (!session || !zeroDialog) return;
    const locationName = zeroDialog.locationName;
    const requests = item.kind === 'standard'
      ? [{ itemBatchNo: undefined as string | undefined, itemBatchExpDate: undefined as Date | undefined, itemSerialNo: undefined as string | undefined }]
      : item.rows.map(r => ({
          itemBatchNo: item.kind === 'batch' ? r.batchNo : undefined,
          itemBatchExpDate: item.kind === 'batch' && r.expiryDate ? new Date(r.expiryDate) : undefined,
          itemSerialNo: item.kind === 'serial' ? r.serialNo : undefined
        }));

    const newLines: InventoryCountLine[] = [];
    try {
      for (const req of requests) {
        const newLine = await inventoryService.addCountLine(session.batchID, {
          itemNo: item.itemNo,
          locationName,
          quantity: 0,
          description1: item.description,
          warehouse: session.warehouse,
          itemBatchNo: req.itemBatchNo,
          itemBatchExpDate: req.itemBatchExpDate,
          itemSerialNo: req.itemSerialNo
        });
        newLines.push(newLine);
      }
    } finally {
      // Også ved delvis feil: linjene som ble lagret skal vises
      if (newLines.length > 0) {
        setSession((prev: InventorySession | null) => prev ? {
          ...prev,
          lines: [...[...newLines].reverse(), ...prev.lines],
          transactionCount: prev.transactionCount + newLines.length
        } : null);
      }
    }
  };

  const handleZeroDialogCount = (item: LocationItem) => {
    const pendingNext = zeroDialog?.nextLocation ?? null;
    setZeroDialog(null);
    if (enableRapidScanning && isLocationLocked) {
      // Hurtigskanning: telling skjer ved å skanne varen
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.locationItems.scanItemToCount', { '0': item.itemNo }, 'Skann {0} for å telle den'),
        validationType: 'success'
      }));
      return;
    }
    handleScanInput(item.itemNo).then(() => {
      if (pendingNext) {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.locationItems.countThenRescan', { '0': item.itemNo, '1': pendingNext }, 'Tell {0}. Skann {1} på nytt når du er ferdig.'),
          validationType: 'success'
        }));
      }
    });
  };

  const handleZeroDialogFinish = () => {
    const dialog = zeroDialog;
    setZeroDialog(null);
    if (!dialog) return;
    proceedFromLocation(dialog.locationName, dialog.nextLocation);
  };

  const handleShowItemsOnLocationChange = (value: boolean) => {
    setShowItemsOnLocation(value);
    try { localStorage.setItem(showItemsStorageKey, value ? '1' : '0'); } catch { /* localStorage utilgjengelig */ }
  };

  const handleRapidScanningChange = async (value: boolean) => {
    setSettingsSaveError(null);
    const ok = await rapidScanningSetting.setValue(value);
    if (!ok) setSettingsSaveError(t('warehouse.modules.inventory.settings.saveFailed', {}, 'Kunne ikke lagre innstillingen. Prøv igjen.'));
  };

  useEffect(() => {
    if (!sessionInitialized.current) {
      loadInventorySession();
      sessionInitialized.current = true;
    }
  }, [user?.operatorId]);

  // Lager valgt (eller lagerlista med ett lager lastet) etter at tellingen ventet på det → start tellingen nå.
  // warehouseRequired er med fordi lagerlista kan bli lastet mens første oppslag fortsatt pågår.
  useEffect(() => {
    if (warehouseRequired && countingWarehouseId && !session) {
      loadInventorySession();
    }
  }, [countingWarehouseId, warehouseRequired]);

  // Avvist tellelinje fra API-et (f.eks. lokasjon i et annet lager) vises som den er
  const countLineErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error && error.name === 'CountLineRejected' ? error.message : fallback;

  // Auto-focus disabled - global scanning is active
  // useEffect(() => {
  //   if (scanInputRef.current && !loading) {
  //     scanInputRef.current.focus();
  //   }
  // }, [loading]);

  useEffect(() => {
    // Auto-focus på edit input når editing mode starter for en ny linje.
    // Bruker lineID som dependency så effekten ikke re-selekterer teksten
    // på hvert tastetrykk (quantity-endringer skaper nytt editingLine-objekt).
    if (editingLine && editQuantityInputRef.current) {
      editQuantityInputRef.current.focus();
      editQuantityInputRef.current.select();
    }
  }, [editingLine?.lineID]);

  // Filter tellinger basert på varenummer og lokasjonskoder
  const filteredLines = session?.lines.filter((line: InventoryCountLine) => {
    if (!filterText.trim()) return true;

    const searchTerm = filterText.toLowerCase();
    const itemNo = line.itemNo.toLowerCase();
    const locationName = line.locationName.toLowerCase();
    const description = (line.description1 || '').toLowerCase();

    const serialNo = (line.itemSerialNo || '').toLowerCase();

    return itemNo.includes(searchTerm) ||
           locationName.includes(searchTerm) ||
           description.includes(searchTerm) ||
           serialNo.includes(searchTerm);
  }) || [];

  // Detect if any modal is open to disable global scanning
  const isAnyModalOpen = showItemSearchModal || showLocationSearchModal || showSettingsModal || showSerialCountingModal || showBatchCountingModal || !!zeroDialog || !!highQuantityWarning;

  // Check if global barcode scanning is enabled via system setting
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Global barcode scanner - processes scans without requiring input focus
  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      processGlobalBarcode(barcode);
    }
  });

  // Process global barcode scan - replicates input box parsing logic
  const processGlobalBarcode = async (value: string) => {
    if (!value.trim()) return;

    // Prevent double-scanning - ignore new scans while processing
    if (formData.isScanning) {
      console.log('⏸️ Already processing scan, ignoring new input (global)');
      return;
    }

    // Call the existing handleScanInput function which already has all the logic
    await handleScanInput(value);
  };

  const loadInventorySession = async () => {
    if (!user?.operatorId || sessionLoading) return; // Forhindre multiple kall

    setSessionLoading(true);
    setLoading(true);

    try {
      console.log('Loading inventory session for operator:', user.operatorId);

      // Søk etter eksisterende aktiv session
      const activeSession = await inventoryService.getActiveInventorySession(user.operatorId);
      
      if (activeSession) {
        console.log('Found existing active session:', activeSession.batchID);
        setSession(activeSession);
      } else if (!countingWarehouseId) {
        // «Alle lager» (eller lagerlista ikke lastet ennå): tidligere ble tellingen stille opprettet på
        // lager 1, og lokasjoner fra andre lagre havnet med feil lager i saldo og ERP-fil (Strømmes24 22.10.2026)
        setWarehouseRequired(true);
      } else {
        console.log('No active session found, creating new one');
        setWarehouseRequired(false);
        const newSession = await inventoryService.createInventorySession(user.operatorId, countingWarehouseId);
        console.log('Created new session:', newSession.batchID);
        setSession(newSession);
      }
    } catch (error) {
      console.error('Feil ved lasting av tellesession:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.loadSessionFailed'),
        validationType: 'error'
      }));
    } finally {
      setSessionLoading(false);
      setLoading(false);
    }
  };

  const handleScanInput = async (value: string) => {
    if (!value.trim()) return;

    // Prevent double-scanning - ignore new scans while processing
    if (formData.isScanning) {
      console.log('⏸️ Already processing scan, ignoring new input');
      return;
    }

    setFormData((prev: InventoryFormData) => ({ ...prev, isScanning: true, validationMessage: '' }));

    try {
      // Check if input looks like GS1-128 (contains separators or has ]C1 prefix)
      const looksLikeGS1 = value.includes(String.fromCharCode(29)) || value.includes('~') || value.includes('§') || value.startsWith(']C1');

      console.log('Raw scan input:', value, 'Looks like GS1:', looksLikeGS1);

      let scannedItemNo = value; // Default to raw value
      let scannedQuantity: number | undefined = undefined;

      if (looksLikeGS1) {
        // Try parsing as GS1-128
        const gs1Data = parseGS1Barcode(value);
        console.log('GS1 parse result:', gs1Data);

        // Check if we got valid GS1 data with GTIN
        const hasGS1Data = gs1Data.parsedAIs && Object.keys(gs1Data.parsedAIs).length > 0;

        if (hasGS1Data && gs1Data.gtin) {
          // GS1-128 barcode with GTIN
          console.log('✓ GS1-128 format detected');
          scannedItemNo = gs1Data.gtin;
          scannedQuantity = gs1Data.quantity;
        } else {
          // Had separators but no valid GS1 - strip separators
          console.log('✓ Had separators but not valid GS1, stripping separators');
          scannedItemNo = value.replace(/[~§]+$/g, '').trim();
        }
      } else {
        // Plain barcode (just item number or location)
        console.log('✓ Plain barcode format detected');
        // Strip scanner-specific separator characters from plain barcodes
        scannedItemNo = value.replace(/[~§]+$/g, '').trim();
      }

      // RAPID SCANNING MODE: Auto-increment quantity for matching item at locked location
      if (enableRapidScanning && isLocationLocked && lockedLocation) {
        // In rapid scanning mode, scanned value should be an item
        const itemValidation = await inventoryService.validateItem(scannedItemNo);

        if (!itemValidation.valid) {
          // TW 36413029: med «Vis varer på lokasjon» prøves et skann som ikke er vare som lokasjon → lokasjonsbytte
          if (itemsOnLocationMode && session) {
            const locationValidation = await inventoryService.validateLocationInWarehouse(scannedItemNo, session.warehouse || currentWarehouse || '');
            if (locationValidation.valid) {
              await leaveActiveLocation(locationValidation.locationName);
              return;
            }
          }
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.errors.itemNotFound', { '0': scannedItemNo }),
            validationType: 'error'
          }));
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
          return;
        }

        // Check if a count line already exists for this item + location
        const existingLine = session?.lines.find(
          line => line.itemNo?.toLowerCase() === itemValidation.itemNo.toLowerCase() &&
                  line.locationName?.toLowerCase() === lockedLocation.locationName.toLowerCase()
        );

        // Use quantity from GS1 if available, otherwise default to 1
        const quantityToAdd = scannedQuantity || 1;

        if (existingLine) {
          // Increment quantity
          const newQuantity = existingLine.quantity + quantityToAdd;
          try {
            const updatedLine = await inventoryService.updateCountLine(session!.batchID, existingLine.lineID, newQuantity);

            // Oppdater session med oppdatert linje
            setSession((prev: InventorySession | null) => prev ? {
              ...prev,
              lines: prev.lines.map(line =>
                line.lineID === existingLine.lineID ? updatedLine : line
              )
            } : null);

            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.messages.quantityUpdated', { '0': itemValidation.itemNo, '1': newQuantity }),
              validationType: 'success'
            }));
          } catch (error) {
            console.error('Feil ved oppdatering av tellelinje:', error);
            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.errors.updateQuantityFailed'),
              validationType: 'error'
            }));
          }
        } else {
          // Create new count line with scanned quantity
          try {
            const countData = {
              itemNo: itemValidation.itemNo,
              locationName: lockedLocation.locationName,
              quantity: quantityToAdd,
              description1: itemValidation.description1 || '',
              warehouse: session!.warehouse
            };

            const newLine = await inventoryService.addCountLine(session!.batchID, countData);

            // Oppdater session med ny linje
            setSession((prev: InventorySession | null) => prev ? {
              ...prev,
              lines: [newLine, ...prev.lines],
              transactionCount: prev.transactionCount + 1
            } : null);

            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.messages.quantityUpdatedNew', { '0': itemValidation.itemNo, '1': quantityToAdd }),
              validationType: 'success'
            }));
          } catch (error) {
            console.error('Feil ved adding av tellelinje:', error);
            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: countLineErrorMessage(error, t('warehouse.modules.inventory.errors.addCountLineFailed')),
              validationType: 'error'
            }));
          }
        }

        // Auto-focus disabled - global scanning is active
        // setTimeout(() => scanInputRef.current?.focus(), 100);
        return;
      }

      // NORMAL MODE: Try as item first
      const itemValidation = await inventoryService.validateItem(scannedItemNo);
      if (itemValidation.valid) {
        // TW 36413029: lista er per lokasjon — lokasjonen må skannes først
        if (itemsOnLocationMode && !currentLocation) {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.errors.scanLocationFirst', {}, 'Skann lokasjon først'),
            validationType: 'error'
          }));
          return;
        }
        setCurrentItem({
          itemNo: itemValidation.itemNo,
          description: itemValidation.description1 || '',
          itemType: itemValidation.itemType
        });
        setItemImageFor(itemValidation.itemPictureName);

        // If GS1 quantity is present, set it in the form
        if (typeof scannedQuantity === 'number' && scannedQuantity > 0) {
          const qty = scannedQuantity; // Type narrowing
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            quantity: qty.toString(),
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.messages.itemFoundWithQuantity', { '0': itemValidation.description1 || '', '1': qty }),
            validationType: 'success'
          }));
        } else {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.messages.itemFound', { '0': itemValidation.description1 || '' }),
            validationType: 'success'
          }));
        }

        // GO-fritelling: lokasjon er valgfri — hent foreslått fast lokasjon (kun tekst/standard)
        if (locationOptional && !currentLocation) {
          setSuggestedLocation(null);
          inventoryService.getSuggestedLocation(itemValidation.itemNo, currentWarehouse)
            .then(loc => setSuggestedLocation(loc))
            .catch(() => setSuggestedLocation(null));
        }

        // If both item and location are scanned, focus on quantity (unless GS1 has quantity)
        // Otherwise focus back to scanner. In GO-fritelling location is optional → go straight to quantity.
        if (currentLocation || locationOptional) {
          // If GS1 provided quantity and we have both item and location, we can add directly
          if (typeof scannedQuantity === 'number' && scannedQuantity > 0) {
            // Auto-submit if we have all data
            setTimeout(() => handleAddCount(), 100);
          } else {
            setTimeout(() => quantityInputRef.current?.focus(), 100);
          }
        } else {
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
        }
        return;
      }

      // TW 36413029: lokasjonen løses i valgt lager, og et nytt lokasjonsskann avslutter den aktive
      if (itemsOnLocationMode) {
        const locationInWarehouse = await inventoryService.validateLocationInWarehouse(scannedItemNo, session?.warehouse || currentWarehouse || '');
        if (locationInWarehouse.valid) {
          await leaveActiveLocation(locationInWarehouse.locationName);
          return;
        }
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          isScanning: false,
          validationMessage: t('warehouse.modules.inventory.errors.barcodeNotFound', { '0': value }),
          validationType: 'error'
        }));
        return;
      }

      // Try as location - use the same cleaned scannedItemNo value.
      // Lokasjonen løses i tellingens lager: tidligere ble en lokasjon fra et hvilket som helst lager
      // godtatt og talt med tellingens lager (Strømmes24 22.10.2026).
      const countWarehouse = session?.warehouse || currentWarehouse || '';
      const locationValidation = await inventoryService.validateLocationInWarehouse(scannedItemNo, countWarehouse);
      if (!locationValidation.valid) {
        const locationElsewhere = await inventoryService.validateLocation(scannedItemNo);
        if (locationElsewhere.valid) {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t(
              'warehouse.modules.inventory.errors.locationInOtherWarehouse',
              { '0': locationElsewhere.locationName, '1': locationElsewhere.warehouseID ?? '', '2': countWarehouse },
              'Lokasjon {0} tilhører lager {1}, men tellingen gjelder lager {2}. Avslutt tellingen og start en ny på lager {1}.'
            ),
            validationType: 'error'
          }));
          return;
        }
      }
      if (locationValidation.valid) {
        setCurrentLocation({
          locationName: locationValidation.locationName
        });
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          isScanning: false,
          validationMessage: t('warehouse.modules.inventory.messages.locationFound', { '0': locationValidation.locationName }),
          validationType: 'success'
        }));
        // If both item and location are scanned, focus on quantity (unless GS1 already set quantity)
        // Otherwise focus back to scanner
        if (currentItem) {
          // If GS1 provided quantity and we have both item and location, we can add directly
          if (formData.quantity && parseFloat(formData.quantity) > 0) {
            setTimeout(() => handleAddCount(), 100);
          } else {
            setTimeout(() => quantityInputRef.current?.focus(), 100);
          }
        } else {
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
        }
        return;
      }

      // No match found
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.errors.barcodeNotFound', { '0': value }),
        validationType: 'error'
      }));
      // Auto-focus disabled - global scanning is active
      // setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved validering:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.errors.validationFailed'),
        validationType: 'error'
      }));
      // Auto-focus disabled - global scanning is active
      // setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const handleAddCount = async (forceOverride = false) => {
    if (!session || !currentItem || (!currentLocation && !locationOptional)) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: locationOptional
          ? t('warehouse.modules.inventory.errors.scanItemFirst', {}, 'Du må skanne vare først')
          : t('warehouse.modules.inventory.errors.scanItemAndLocationFirst'),
        validationType: 'error'
      }));
      return;
    }

    // GO-fritelling: skannet lokasjon vinner, ellers foreslått fast lokasjon, ellers tom
    const effectiveLocationName = currentLocation?.locationName ?? suggestedLocation ?? '';

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity < 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.validQuantityRequired'),
        validationType: 'error'
      }));
      // Fokus på quantity ved feil
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    if (!forceOverride && maxLineItemQuantity != null && quantity > maxLineItemQuantity) {
      setHighQuantityWarning({ source: 'add', quantity });
      return;
    }

    setIsAdding(true);
    try {
      const countData = {
        itemNo: currentItem.itemNo,
        locationName: effectiveLocationName,
        quantity,
        description1: currentItem.description,
        warehouse: session.warehouse
      };

      const newLine = await inventoryService.addCountLine(session.batchID, countData);

      // Oppdater session med ny linje
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [newLine, ...prev.lines],
        transactionCount: prev.transactionCount + 1
      } : null);

      // Track last counted line for optional capacity update (non-intrusive, like picking)
      const countedItemNo = currentItem.itemNo;
      const countedItemDesc = currentItem.description;
      const countedBinCode = effectiveLocationName;
      const countedQty = quantity;

      // Reset form for neste telling
      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: effectiveLocationName
          ? t('warehouse.modules.inventory.messages.countAdded', { '0': currentItem.itemNo, '1': quantity, '2': effectiveLocationName })
          : t('warehouse.modules.inventory.messages.countAddedNoLocation', { '0': currentItem.itemNo, '1': quantity }, 'Telling lagt til: {0} x {1}'),
        validationType: 'success'
      });
      setCurrentItem(null);
      // TW 36413029: med «Vis varer på lokasjon» blir lokasjonen stående som aktiv
      if (!itemsOnLocationMode) setCurrentLocation(null);
      setSuggestedLocation(null);
      setCurrentItemImageUrl(null);

      // Track for optional capacity button (only for advanced warehouses)
      if (isAdvancedWarehouse) {
        setLastCountedInfo({
          itemNo: countedItemNo,
          itemDescription: countedItemDesc,
          binCode: countedBinCode,
          quantity: countedQty,
        });
        // Auto-hide after 15 seconds
        setTimeout(() => setLastCountedInfo(null), 15000);
      }

      // Antallsfeltet unmountes ved reset og tar fokuset med seg — uten dette fanger
      // hverken den usynlige capture-inputen (Zebra o.l.) eller operatøren neste skann
      // uten et manuelt tapp på skjermen. Scroll til toppen så skann-statusen er synlig
      // på små terminaler.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => mobileCaptureRef.current?.focus(), 150);

    } catch (error) {
      console.error('Feil ved adding av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: countLineErrorMessage(error, t('warehouse.modules.inventory.errors.addCountLineFailed')),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleStartEdit = (line: InventoryCountLine) => {
    setEditingLine({
      lineID: line.lineID,
      quantity: line.quantity.toString()
    });
  };

  const handleCancelEdit = () => {
    setEditingLine(null);
  };

  const handleSaveEdit = async (forceOverride = false) => {
    if (!editingLine || !session) return;

    const quantity = parseFloat(editingLine.quantity);
    if (isNaN(quantity) || quantity < 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.validQuantityRequired'),
        validationType: 'error'
      }));
      return;
    }

    if (!forceOverride && maxLineItemQuantity != null && quantity > maxLineItemQuantity) {
      setHighQuantityWarning({ source: 'edit', quantity });
      return;
    }

    setUpdatingLineId(editingLine.lineID);
    try {
      const updatedLine = await inventoryService.updateCountLine(session.batchID, editingLine.lineID, quantity);
      
      // Oppdater session med oppdatert linje
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: prev.lines.map(line => 
          line.lineID === editingLine.lineID ? updatedLine : line
        )
      } : null);

      setEditingLine(null);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.quantityUpdatedSimple', { '0': quantity }),
        validationType: 'success'
      }));

      // Clear message after 3 seconds
      setTimeout(() => {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: '',
          validationType: ''
        }));
      }, 3000);

    } catch (error) {
      console.error('Feil ved oppdatering av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.updateCountLineFailed', {}, 'Kunne ikke oppdatere tellelinje'),
        validationType: 'error'
      }));
    } finally {
      setUpdatingLineId(null);
    }
  };

  // Advarsel for høyt antall: «Ja» lagrer likevel, «Nei» gir fokus tilbake til antallsfeltet
  const handleHighQuantityConfirm = () => {
    const source = highQuantityWarning?.source;
    setHighQuantityWarning(null);
    if (source === 'add') handleAddCount(true);
    else if (source === 'edit') handleSaveEdit(true);
  };

  const handleHighQuantityCancel = () => {
    const source = highQuantityWarning?.source;
    setHighQuantityWarning(null);
    const inputRef = source === 'edit' ? editQuantityInputRef : quantityInputRef;
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 100);
  };

  const handleDeleteLine = async (line: InventoryCountLine) => {
    if (!session) return;

    const confirmDelete = window.confirm(
      t('warehouse.modules.inventory.confirmations.deleteCount', { '0': line.itemNo, '1': line.description1, '2': line.locationName, '3': line.quantity })
    );
    if (!confirmDelete) return;

    setDeletingLineId(line.lineID);
    try {
      const success = await inventoryService.deleteCountLine(session.batchID, line.lineID);
      
      if (success) {
        // Fjern linje fra session
        setSession((prev: InventorySession | null) => prev ? {
          ...prev,
          lines: prev.lines.filter(l => l.lineID !== line.lineID),
          transactionCount: prev.transactionCount - 1
        } : null);

        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.messages.countDeleted', { '0': line.itemNo }),
          validationType: 'success'
        }));

        // Clear message after 3 seconds
        setTimeout(() => {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            validationMessage: '',
            validationType: ''
          }));
        }, 3000);
      }

    } catch (error) {
      console.error('Feil ved sletting av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.deleteCountLineFailed'),
        validationType: 'error'
      }));
    } finally {
      setDeletingLineId(null);
    }
  };

  const handleReset = () => {
    setFormData({
      scannedCode: '',
      quantity: '',
      isScanning: false,
      validationMessage: '',
      validationType: ''
    });
    setCurrentItem(null);
    // TW 36413029: lokasjonen forlates via «Ferdig med lokasjon» eller nytt lokasjonsskann
    if (!itemsOnLocationMode) setCurrentLocation(null);
    setSuggestedLocation(null);
    setCurrentItemImageUrl(null);
    // Auto-focus disabled - global scanning is active
    // setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleToggleLocationLock = () => {
    if (isLocationLocked) {
      // Unlock
      setIsLocationLocked(false);
      setLockedLocation(null);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.locationUnlocked'),
        validationType: 'success'
      }));
    } else {
      // Lock - require current location to be set
      if (!currentLocation) {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.errors.scanLocationBeforeLocking'),
          validationType: 'error'
        }));
        return;
      }
      setIsLocationLocked(true);
      setLockedLocation(currentLocation);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.locationLocked', { '0': currentLocation.locationName }),
        validationType: 'success'
      }));
    }
    // Auto-focus disabled - global scanning is active
    // setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleItemSearchSelect = async (item: ItemsDto, location: LocationInfo) => {
    setShowItemSearchModal(false);

    // TW 36413029: varen telles på aktiv lokasjon — lokasjonssteget hoppes over i søket
    if (itemsOnLocationMode) {
      if (!currentLocation) {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.errors.scanLocationFirst', {}, 'Skann lokasjon først'),
          validationType: 'error'
        }));
        return;
      }
      setCurrentItem({
        itemNo: item.itemNo,
        description: item.description1 || '',
        itemType: item.itemType ?? undefined
      });
      setItemImageFor(item.itemPictureName);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.itemFound', { '0': item.description1 || '' }),
        validationType: 'success'
      }));
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    // Set item and location from search modal
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description1 || '',
      itemType: item.itemType ?? undefined
    });
    setItemImageFor(item.itemPictureName);

    if (!location.locationName) {
      // GO-fritelling: lokasjonssteget ble hoppet over — vis foreslått fast lokasjon som tekst
      setSuggestedLocation(item.stdBinCode?.trim() || null);
      if (!item.stdBinCode?.trim()) {
        inventoryService.getSuggestedLocation(item.itemNo, currentWarehouse)
          .then(loc => setSuggestedLocation(loc))
          .catch(() => setSuggestedLocation(null));
      }
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.itemFound', { '0': item.description1 || '' }),
        validationType: 'success'
      }));
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    setCurrentLocation({
      locationName: location.locationName
    });
    setFormData((prev: InventoryFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventory.messages.itemAndLocationSet', { '0': item.itemNo, '1': location.locationName, '2': location.availableQuantity }),
      validationType: 'success'
    }));

    // Focus will automatically go to quantity field
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleLocationSearchSelect = async (location: LocationSearchResult) => {
    setShowLocationSearchModal(false);

    // TW 36413029: lokasjonen må finnes i valgt lager, og et bytte avslutter den aktive
    if (itemsOnLocationMode) {
      const validation = await inventoryService.validateLocationInWarehouse(location.binCode, session?.warehouse || currentWarehouse || '');
      if (!validation.valid) {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.errors.locationNotInWarehouse', { '0': location.binCode }, 'Lokasjon {0} finnes ikke i valgt lager'),
          validationType: 'error'
        }));
        return;
      }
      await leaveActiveLocation(validation.locationName);
      return;
    }

    // Set location
    setCurrentLocation({
      locationName: location.binCode
    });
    setFormData((prev: InventoryFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventory.messages.locationSet', { '0': location.binCode }),
      validationType: 'success'
    }));

    // Only focus quantity field if item is already selected
    if (currentItem) {
      setTimeout(() => quantityInputRef.current?.focus(), 100);
    }
  };

  // Håndter serial-telling resultater
  const handleSerialCountComplete = async (results: SerialCountResult[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowSerialCountingModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCountLine[] = [];
      for (const result of results) {
        const countData = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: result.status === 'missing' ? 0 : 1,
          description1: currentItem.description,
          warehouse: session.warehouse,
          itemSerialNo: result.serialNo
        };

        const newLine = await inventoryService.addCountLine(session.batchID, countData);
        newLines.push(newLine);
      }

      // Oppdater session med alle nye linjer
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      const confirmed = results.filter(r => r.status === 'confirmed').length;
      const missing = results.filter(r => r.status === 'missing').length;
      const newCount = results.filter(r => r.status === 'new').length;
      const parts: string[] = [];
      if (confirmed > 0) parts.push(t('warehouse.modules.inventory.countResult.confirmed', { '0': confirmed }, `${confirmed} bekreftet`));
      if (missing > 0) parts.push(t('warehouse.modules.inventory.countResult.missing', { '0': missing }, `${missing} mangler`));
      if (newCount > 0) parts.push(t('warehouse.modules.inventory.countResult.new', { '0': newCount }, `${newCount} nye`));

      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.messages.serialCountCompleted', { '0': parts.join(', ') }, `Serienummer-telling fullført: ${parts.join(', ')}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      if (!itemsOnLocationMode) setCurrentLocation(null);
    } catch (error) {
      console.error('Feil ved serial-telling:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: countLineErrorMessage(error, t('warehouse.modules.inventory.errors.serialCountSaveFailed', {}, 'Feil ved lagring av serienummer-telling')),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Håndter batch-telling resultater
  const handleBatchCountComplete = async (results: BatchCountResult[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowBatchCountingModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCountLine[] = [];
      for (const result of results) {
        const countData = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: result.quantity,
          description1: currentItem.description,
          warehouse: session.warehouse,
          itemBatchNo: result.batchNo,
          itemBatchExpDate: result.expiryDate ? new Date(result.expiryDate) : undefined
        };

        const newLine = await inventoryService.addCountLine(session.batchID, countData);
        newLines.push(newLine);
      }

      // Oppdater session med alle nye linjer
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      const confirmed = results.filter(r => r.status === 'confirmed').length;
      const missing = results.filter(r => r.status === 'missing').length;
      const newCount = results.filter(r => r.status === 'new').length;
      const parts: string[] = [];
      if (confirmed > 0) parts.push(t('warehouse.modules.inventory.countResult.confirmed', { '0': confirmed }, `${confirmed} bekreftet`));
      if (missing > 0) parts.push(t('warehouse.modules.inventory.countResult.missing', { '0': missing }, `${missing} mangler`));
      if (newCount > 0) parts.push(t('warehouse.modules.inventory.countResult.new', { '0': newCount }, `${newCount} nye`));

      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.messages.batchCountCompleted', { '0': parts.join(', ') }, `Batch-telling fullført: ${parts.join(', ')}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      if (!itemsOnLocationMode) setCurrentLocation(null);
    } catch (error) {
      console.error('Feil ved batch-telling:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: countLineErrorMessage(error, t('warehouse.modules.inventory.errors.batchCountSaveFailed', {}, 'Feil ved lagring av batch-telling')),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Avbryt påbegynt telling (f.eks. startet på feil lager): hode og linjer slettes, og en ny
  // telling startes på lageret som er valgt i lagervelgeren (eller lagervalg kreves)
  const handleCancelSession = async () => {
    if (!session) return;

    // Si hvilket lager som avbrytes, og hvor neste telling havner (valgt lager, eller lagervalg kreves)
    const warehouseLabel = (id: string) => {
      const name = warehouses.find(w => w.warehouseID === id)?.warehouseDescr;
      return name ? `${name} (${id})` : id;
    };
    const current = warehouseLabel(session.warehouse || '');
    const cancelText = session.lines.length > 0
      ? t('warehouse.modules.inventory.cancelSession.confirmWithLines', { '0': current, '1': session.lines.length },
          'Avbryte tellingen på {0}? {1} registrerte tellinger forkastes og sendes ikke til ERP.')
      : t('warehouse.modules.inventory.cancelSession.confirmEmpty', { '0': current }, 'Avbryte tellingen på {0}?');
    const nextText = countingWarehouseId
      ? t('warehouse.modules.inventory.cancelSession.nextWarehouse', { '0': warehouseLabel(countingWarehouseId) },
          'En ny telling startes på {0}.')
      : t('warehouse.modules.inventory.cancelSession.nextSelectWarehouse', {},
          'Du må velge lager før en ny telling startes.');
    const confirmCancel = window.confirm(`${cancelText}\n\n${nextText}`);
    if (!confirmCancel) return;

    try {
      await inventoryService.cancelInventorySession(session.batchID);
      setSession(null);
      setCurrentItem(null);
      setCurrentLocation(null);
      setFormData((prev: InventoryFormData) => ({ ...prev, validationMessage: '', validationType: '' }));
      await loadInventorySession();
    } catch (error) {
      console.error('Feil ved avbryting av telling:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: error instanceof Error && error.message
          ? error.message
          : t('warehouse.modules.inventory.cancelSession.failed', {}, 'Kunne ikke avbryte tellingen'),
        validationType: 'error'
      }));
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    if (session.lines.length === 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.cannotCompleteEmptySession'),
        validationType: 'error'
      }));
      return;
    }

    const confirmComplete = window.confirm(t('warehouse.modules.inventory.confirmations.completeSession', { '0': session.lines.length }));
    if (!confirmComplete) return;

    try {
      const result = await inventoryService.completeInventorySession(session.batchID);
      if (result.success) {
        // Show appropriate message based on whether validation is required
        if (result.requiresValidation) {
          alert(t('warehouse.modules.inventory.messages.countSentForApproval'));
        } else {
          alert(t('warehouse.modules.inventory.messages.countCompletedAndExported'));
        }
        onBack(); // Gå tilbake til dashboard
      } else {
        throw new Error(result.message || t('warehouse.modules.inventory.errors.completeSessionFailed', {}, 'Kunne ikke fullføre tellesession'));
      }
    } catch (error) {
      console.error('Feil ved fullføring:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: error instanceof Error ? error.message : t('warehouse.modules.inventory.errors.completeSessionFailed'),
        validationType: 'error'
      }));
    }
  };

  // Render counting list mode (no session dependency)
  if (countingMode === 'counting-list') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" iconSize={20} label="" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.inventory.title')}</h1>
              </div>
            </div>
          </div>
          {/* Mode tabs */}
          <div className="px-3 sm:px-4 flex space-x-1 border-t bg-gray-50">
            <button
              onClick={() => { setCountingMode('free-counting'); setSelectedCountingList(null); }}
              className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700"
            >
              <span className="flex items-center space-x-1.5">
                <Scan className="h-4 w-4" />
                <span>{t('warehouse.modules.inventory.tabs.freeCounting', {}, 'Fritelling')}</span>
              </span>
            </button>
            <button
              className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600"
            >
              <span className="flex items-center space-x-1.5">
                <ClipboardList className="h-4 w-4" />
                <span>{t('warehouse.modules.inventory.tabs.countList', {}, 'Telleliste')}</span>
              </span>
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-4 max-w-4xl mx-auto">
          {selectedCountingList ? (
            <CountingListCounting
              list={selectedCountingList}
              onBack={() => setSelectedCountingList(null)}
              onCompleted={() => setSelectedCountingList(null)}
            />
          ) : (
            <CountingListSelector
              onSelectList={(list) => setSelectedCountingList(list)}
            />
          )}
        </div>
      </div>
    );
  }

  // Free counting mode
  // (venter også på lagerlista når lager ikke er valgt — ett-lager-tenanter starter tellingen når den er lastet)
  if (loading || (!session && warehouseRequired && warehouses.length <= 1)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-2">{t('warehouse.modules.inventory.status.loadingSession')}</span>
      </div>
    );
  }

  if (!session && warehouseRequired) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-12 w-12 text-amber-700 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">
          {t('warehouse.modules.inventory.errors.selectWarehouseFirst', {}, 'Velg lager før du starter telling')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t('warehouse.modules.inventory.errors.selectWarehouseFirstHint', {}, 'En telling gjelder ett lager. Velg lager i lagervelgeren øverst — «Alle lager» kan ikke brukes til telling.')}
        </p>
        <button
          onClick={onBack}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {t('common.buttons.backToDashboard')}
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">{t('warehouse.modules.inventory.errors.loadSessionFailed')}</h2>
        <button
          onClick={onBack}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {t('common.buttons.backToDashboard')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        ref={mobileCaptureRef}
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);
          processGlobalBarcode(barcode);
        }}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" iconSize={20} label="" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.inventory.title')}</h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{session.description1}</p>
              {/* Tellingens lager — lagervelgeren øverst kan stå på «Alle lager» eller et annet lager */}
              {session.warehouse && (
                <p className="text-xs sm:text-sm font-medium text-blue-700 truncate">
                  {(() => {
                    const name = warehouses.find(w => w.warehouseID === session.warehouse)?.warehouseDescr;
                    const label = name ? `${name} (${session.warehouse})` : session.warehouse;
                    return t('warehouse.modules.inventory.countList.warehouseLabel', { '0': label }, `Lager: ${label}`);
                  })()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            <span className="text-xs sm:text-sm bg-blue-100 text-blue-800 px-1.5 sm:px-2 py-1 rounded whitespace-nowrap">
              {session.lines.length}
            </span>
            <MediaCaptureButton
              entityType="InventoryCounting"
              entityKey={session.batchID}
            />
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px]"
              title={t('common.buttons.settings')}
            >
              <Settings className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={handleCancelSession}
              className="border border-red-300 text-red-600 hover:bg-red-100 px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm flex items-center space-x-1 min-h-[44px]"
              title={t('warehouse.modules.inventory.cancelSession.button', {}, 'Avbryt telling')}
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">{t('warehouse.modules.inventory.cancelSession.button', {}, 'Avbryt telling')}</span>
            </button>
            {session.lines.length > 0 && (
              <button
                onClick={handleCompleteSession}
                className="bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-green-600 flex items-center space-x-1 min-h-[44px]"
              >
                <Save className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.buttons.complete')}</span>
              </button>
            )}
          </div>
        </div>
        {/* Mode tabs */}
        <div className="px-3 sm:px-4 flex space-x-1 border-t bg-gray-50">
          <button
            className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600"
          >
            <span className="flex items-center space-x-1.5">
              <Scan className="h-4 w-4" />
              <span>{t('warehouse.modules.inventory.tabs.freeCounting', {}, 'Fritelling')}</span>
            </span>
          </button>
          <button
            onClick={() => setCountingMode('counting-list')}
            className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700"
          >
            <span className="flex items-center space-x-1.5">
              <ClipboardList className="h-4 w-4" />
              <span>{t('warehouse.modules.inventory.tabs.countList', {}, 'Telleliste')}</span>
            </span>
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-3 sm:space-y-4">
        {/* Toolbar - hide when rapid scanning with locked location */}
        {!(enableRapidScanning && isLocationLocked) && (
          <div className="bg-white rounded-lg p-3 shadow-sm border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Scan className="h-5 w-5 text-blue-500" />
                <span className="font-medium text-gray-700 text-sm sm:text-base">{t('warehouse.modules.inventory.quickSelect.title')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowItemSearchModal(true)}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm min-h-[44px] flex-1 sm:flex-initial"
                >
                  <Search className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.quickSelect.searchItem')}</span>
                </button>
                <button
                  onClick={() => setShowLocationSearchModal(true)}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-purple-500 text-white hover:bg-purple-600 rounded-lg transition-colors text-sm min-h-[44px] flex-1 sm:flex-initial"
                  title={t('warehouse.modules.inventory.quickSelect.searchLocation')}
                >
                  <MapPin className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.quickSelect.searchLocationShort')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location lock status banner (only show when rapid scanning is enabled) */}
        {enableRapidScanning && isLocationLocked && lockedLocation && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start sm:items-center min-w-0">
                <Lock className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900 truncate">
                    {t('warehouse.modules.inventory.scanning.lockedTo', { '0': lockedLocation.locationName })}
                  </p>
                  <p className="text-xs text-blue-700">
                    {t('warehouse.modules.inventory.scanning.scanItemsToIncreaseQuantity')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleLocationLock}
                className="flex items-center justify-center space-x-1 px-3 py-2.5 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm min-h-[44px] w-full sm:w-auto flex-shrink-0"
              >
                <Unlock className="h-4 w-4" />
                <span>{t('warehouse.modules.inventory.scanning.unlock')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Scanning og Input sektor */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="font-semibold mb-3 flex items-center text-sm sm:text-base">
            <Scan className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0" />
            <span className="truncate">{t('warehouse.modules.inventory.scanning.scanItemOrLocation')}</span>
          </h2>

          <div className="space-y-3">
            {/* Scanner input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (formData.scannedCode.trim()) {
                  handleScanInput(formData.scannedCode.trim());
                  setFormData((prev: InventoryFormData) => ({ ...prev, scannedCode: '' }));
                }
              }}
              className="relative"
            >
              <input
                ref={scanInputRef}
                type="text"
                value={formData.scannedCode}
                onChange={(e) => setFormData((prev: InventoryFormData) => ({ ...prev, scannedCode: e.target.value }))}
                placeholder={
                  enableRapidScanning && isLocationLocked
                    ? t('warehouse.modules.inventory.scanning.scanItemForLocation', { '0': lockedLocation?.locationName || '' })
                    : itemsOnLocationMode && !currentLocation
                      ? t('warehouse.modules.inventory.scanning.scanLocationFirstPlaceholder', {}, 'Skann lokasjon')
                      : t('warehouse.modules.inventory.scanning.scanBarcode')
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                disabled={formData.isScanning || showItemSearchModal || showLocationSearchModal}
                enterKeyHint="go"
              />
              {formData.isScanning && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader className="animate-spin h-5 w-5 text-blue-500" />
                </div>
              )}
            </form>

            {/* Validation message */}
            {formData.validationMessage && (
              <div className={`p-2 rounded flex items-start text-xs sm:text-sm ${
                formData.validationType === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}>
                {formData.validationType === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <span className="break-words">{formData.validationMessage}</span>
              </div>
            )}

            {/* Current selections */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Current Item */}
              <div className={`p-3 border rounded-lg ${currentItem ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center">
                  <Package className={`h-4 w-4 mr-2 flex-shrink-0 ${currentItem ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-sm">{t('warehouse.general.item')}</span>
                </div>
                {currentItem ? (
                  <div className="mt-1 min-w-0">
                    <div className="font-mono text-xs sm:text-sm truncate">{currentItem.itemNo}</div>
                    <div className="text-xs text-gray-600 truncate">{currentItem.description}</div>
                  </div>
                ) : (
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{t('warehouse.modules.inventory.status.notScanned')}</div>
                )}
              </div>

              {/* Current Location */}
              <div className={`p-3 border rounded-lg ${
                isLocationLocked && lockedLocation
                  ? 'bg-blue-50 border-blue-300'
                  : currentLocation
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0">
                    {isLocationLocked && lockedLocation ? (
                      <Lock className="h-4 w-4 mr-2 flex-shrink-0 text-blue-600" />
                    ) : (
                      <MapPin className={`h-4 w-4 mr-2 flex-shrink-0 ${currentLocation ? 'text-green-600' : 'text-gray-400'}`} />
                    )}
                    <span className="font-medium text-sm">{t('warehouse.general.location')}</span>
                  </div>
                  {/* Lock/Unlock button (only show when rapid scanning is enabled and location is set) */}
                  {enableRapidScanning && (currentLocation || lockedLocation) && (
                    <button
                      onClick={handleToggleLocationLock}
                      className={`p-1.5 rounded transition-colors flex-shrink-0 min-h-[36px] ${
                        isLocationLocked
                          ? 'text-blue-600 hover:bg-blue-100'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title={isLocationLocked ? t('warehouse.modules.inventory.actions.unlockLocation') : t('warehouse.modules.inventory.actions.lockLocation')}
                    >
                      {isLocationLocked ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <Unlock className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
                {(currentLocation || lockedLocation) ? (
                  <div className="mt-1 min-w-0">
                    <div className="font-mono text-xs sm:text-sm truncate">
                      {isLocationLocked && lockedLocation
                        ? lockedLocation.locationName
                        : currentLocation?.locationName}
                    </div>
                    {isLocationLocked && (
                      <div className="text-xs text-blue-600 mt-1">{t('warehouse.modules.inventory.status.locked')}</div>
                    )}
                  </div>
                ) : locationOptional ? (
                  /* GO-fritelling: lokasjon er valgfri — fast lokasjon vises som hjelpetekst, ikke som valg */
                  <div className="mt-1 min-w-0">
                    {currentItem && suggestedLocation ? (
                      <div className="font-mono text-xs sm:text-sm truncate">
                        {t('warehouse.modules.inventory.status.suggestedLocation', { '0': suggestedLocation }, 'Fast lokasjon: {0}')}
                      </div>
                    ) : null}
                    <div className="text-xs text-gray-500">
                      {t('warehouse.modules.inventory.status.locationOptional', {}, 'Valgfri i dette lageret')}
                    </div>
                  </div>
                ) : itemsOnLocationMode ? (
                  <div className="text-xs sm:text-sm text-purple-700 mt-1">{t('warehouse.modules.inventory.status.scanLocationFirstHint', {}, 'Skann lokasjon først')}</div>
                ) : (
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{t('warehouse.modules.inventory.status.notScanned')}</div>
                )}
              </div>
            </div>

            {/* Serial counting button for ItemType 6 */}
            {currentItem && currentLocation && currentItem.itemType === 6 && !(enableRapidScanning && isLocationLocked) && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowSerialCountingModal(true)}
                  disabled={isAdding}
                  className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-teal-700 transition-colors disabled:opacity-50 min-h-[44px]"
                >
                  {isAdding ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Hash className="h-4 w-4" />
                  )}
                  <span>{t('warehouse.modules.inventory.actions.countSerials', {}, 'Tell serienumre')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600 flex items-center justify-center gap-2 min-h-[44px]"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.actions.resetSelection', {}, 'Nullstill')}</span>
                </button>
              </div>
            )}

            {/* Batch counting button for ItemType 4/5 */}
            {currentItem && currentLocation && (currentItem.itemType === 4 || currentItem.itemType === 5) && !(enableRapidScanning && isLocationLocked) && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowBatchCountingModal(true)}
                  disabled={isAdding}
                  className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-orange-700 transition-colors disabled:opacity-50 min-h-[44px]"
                >
                  {isAdding ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Package className="h-4 w-4" />
                  )}
                  <span>{t('warehouse.modules.inventory.actions.countBatches', {}, 'Tell batcher')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600 flex items-center justify-center gap-2 min-h-[44px]"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.actions.resetSelection', {}, 'Nullstill')}</span>
                </button>
              </div>
            )}

            {/* Quantity input og buttons - kun vis når både vare og lokasjon er skannet OG ikke i rapid scanning mode OG ikke serial/batch-vare */}
            {currentItem && (currentLocation || locationOptional) && !(currentItem.itemType === 4 || currentItem.itemType === 5 || currentItem.itemType === 6) && !(enableRapidScanning && isLocationLocked) && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (currentItem && (currentLocation || locationOptional) && formData.quantity.trim()) {
                    handleAddCount();
                  }
                }}
                className="space-y-2"
              >
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t('common.fields.quantity')}
                  </label>
                  <NumericInput
                    ref={quantityInputRef}
                    mode="decimal"
                    value={formData.quantity}
                    onChange={(e) => setFormData((prev: InventoryFormData) => ({ ...prev, quantity: e.target.value }))}
                    placeholder={t('warehouse.modules.inventory.placeholders.enterQuantity')}
                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                    min="0.01"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    disabled={!currentItem || (!currentLocation && !locationOptional) || !formData.quantity.trim() || isAdding}
                    className={`flex-1 px-4 py-2.5 rounded font-medium flex items-center justify-center space-x-2 text-sm min-h-[44px] ${
                      currentItem && (currentLocation || locationOptional) && formData.quantity.trim() && !isAdding
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isAdding ? (
                      <Loader className="animate-spin h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    <span>{t('common.buttons.add')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    className="p-2.5 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('common.buttons.reset')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* TW 36413029: «Vis varer på lokasjon» — utalte varer åpne, talte kollapset */}
        {itemsOnLocationMode && activeLocationName && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${locationCardCollapsed ? '' : 'border-b'}`}>
              {/* Overskrift + framdrift — trykk for å kollapse/utvide lista (huskes) */}
              <button
                type="button"
                onClick={toggleLocationCardCollapsed}
                aria-expanded={!locationCardCollapsed}
                className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-left min-h-[44px] rounded-lg hover:bg-gray-50 -m-1 p-1"
              >
                <span className="font-semibold flex items-center text-sm sm:text-base min-w-0 flex-shrink-0">
                  {locationCardCollapsed
                    ? <ChevronRight className="h-4 w-4 mr-1 flex-shrink-0 text-gray-500" />
                    : <ChevronDown className="h-4 w-4 mr-1 flex-shrink-0 text-gray-500" />}
                  <MapPin className="h-5 w-5 mr-2 flex-shrink-0 text-purple-500" />
                  <span className="truncate">
                    {t('warehouse.modules.inventory.locationItems.title', { '0': activeLocationName }, 'Varer på {0}')}
                  </span>
                  {locationItemsLoading && <Loader className="animate-spin h-4 w-4 ml-2 text-gray-400 flex-shrink-0" />}
                </span>
                {listIsForActive && (
                  <span className="flex-1 min-w-0 flex flex-col gap-1">
                    <span className="text-xs sm:text-sm text-gray-600 truncate">
                      {locationProgress.total === 0
                        ? t('warehouse.modules.inventory.locationItems.progressEmpty', {}, 'Ingen varer i Compass')
                        : t('warehouse.modules.inventory.locationItems.progress', { '0': locationProgress.counted, '1': locationProgress.total }, '{0} av {1} talt')}
                      {locationProgress.zeroed > 0 && (
                        <> · {t('warehouse.modules.inventory.locationItems.progressZeroed', { '0': locationProgress.zeroed }, '{0} telt til 0')}</>
                      )}
                      {locationProgress.newItems > 0 && (
                        <> · {t('warehouse.modules.inventory.locationItems.progressNew', { '0': locationProgress.newItems }, '{0} ny på lokasjon')}</>
                      )}
                    </span>
                    {locationProgress.total > 0 && (
                      <span className="block h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                        <span
                          className={`block h-full rounded-full transition-all ${locationProgress.counted === locationProgress.total ? 'bg-green-500' : 'bg-purple-500'}`}
                          style={{ width: `${Math.round((locationProgress.counted / locationProgress.total) * 100)}%` }}
                        />
                      </span>
                    )}
                  </span>
                )}
              </button>
              <button
                onClick={() => leaveActiveLocation(null)}
                disabled={formData.isScanning || locationItemsLoading}
                className="flex items-center justify-center gap-1 px-3 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm min-h-[44px] w-full sm:w-auto disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                <span>{t('warehouse.modules.inventory.locationItems.finishLocation', {}, 'Ferdig med lokasjon')}</span>
              </button>
            </div>

            {!locationCardCollapsed && (
              <>
              {locationItemsError && (
                <div className="m-3 p-2 rounded flex items-start text-xs sm:text-sm bg-red-50 text-red-800">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="break-words">{locationItemsError}</span>
                </div>
              )}

              {/* På lokasjon — ikke talt ennå */}
              <div className="px-3 sm:px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('warehouse.modules.inventory.locationItems.onLocation', { '0': uncountedLocationItems.length }, 'På lokasjon ({0})')}
              </div>
              {listIsForActive && uncountedLocationItems.length === 0 && !locationItemsLoading ? (
                <div className="px-3 sm:px-4 pb-3 text-xs sm:text-sm text-gray-500">
                  {(locationItems?.length ?? 0) === 0
                    ? t('warehouse.modules.inventory.locationItems.emptyLocation', {}, 'Compass har ingen varer på lokasjonen')
                    : t('warehouse.modules.inventory.locationItems.allCounted', {}, 'Alle varene er talt')}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                  {/* Ren visning — varen må alltid skannes, raden er ikke trykkbar (kundekrav 2026-10-08) */}
                  {uncountedLocationItems.map(item => (
                    <div
                      key={item.itemNo}
                      className="w-full px-3 sm:px-4 py-3 min-h-[44px] flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-xs sm:text-sm font-semibold truncate">{item.itemNo}</div>
                        <div className="text-xs sm:text-sm text-gray-600 truncate">{item.description}</div>
                        {item.kind !== 'standard' && (
                          <span className={`inline-flex items-center mt-1 text-xs ${item.kind === 'batch' ? 'text-orange-600' : 'text-purple-600'}`}>
                            {item.kind === 'batch' ? <Package className="h-3 w-3 mr-1" /> : <Hash className="h-3 w-3 mr-1" />}
                            {item.kind === 'batch'
                              ? t('warehouse.modules.inventory.locationItems.batchCount', { '0': item.rows.length }, 'Batch · {0} batcher')
                              : t('warehouse.modules.inventory.locationItems.serialCount', { '0': item.rows.length }, 'Serie · {0} serienr')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-700 whitespace-nowrap flex-shrink-0">
                        {t('warehouse.modules.inventory.locationItems.balance', { '0': item.quantity }, 'Saldo: {0}')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Talt — kollapset som standard */}
              <button
                type="button"
                onClick={() => setShowCountedSection(v => !v)}
                className="w-full flex items-center px-3 sm:px-4 py-3 border-t text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-50 min-h-[44px]"
                aria-expanded={showCountedSection}
              >
                {showCountedSection ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
                {t('warehouse.modules.inventory.locationItems.counted', { '0': countedLocationItems.length }, 'Talt ({0})')}
              </button>
              {showCountedSection && countedLocationItems.length > 0 && (
                <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto border-t">
                  {countedLocationItems.map(item => (
                    <div key={item.itemNo} className="px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-xs sm:text-sm font-semibold truncate">{item.itemNo}</div>
                        <div className="text-xs sm:text-sm text-gray-600 truncate">{item.description}</div>
                        {item.isNew ? (
                          <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                            {t('warehouse.modules.inventory.locationItems.newOnLocation', {}, 'Ny på lokasjon')}
                          </span>
                        ) : item.countedQuantity === 0 ? (
                          <span className="inline-block mt-1 text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                            {t('warehouse.modules.inventory.locationItems.countedToZero', {}, 'Telt til 0')}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs sm:text-sm text-right whitespace-nowrap flex-shrink-0">
                        <div className="font-medium text-gray-900">
                          {t('warehouse.modules.inventory.locationItems.countedQuantity', { '0': item.countedQuantity }, 'Talt: {0}')}
                        </div>
                        {item.balance !== undefined && (
                          <div className="text-gray-500">
                            {t('warehouse.modules.inventory.locationItems.balance', { '0': item.balance }, 'Saldo: {0}')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* Varebilde (TW 36391639): mellom aktiv telling og tellelista. Vises kun når vare er
            valgt og bildet finnes; brukket bilde skjules helt. Trykk → full størrelse. */}
        {currentItem && currentItemImageUrl && !itemImageFailed && (
          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm text-gray-700">
                {t('warehouse.modules.inventory.status.itemImage', {}, 'Varebilde')}
              </span>
              <span className="text-xs text-gray-500 font-mono truncate ml-2">{currentItem.itemNo}</span>
            </div>
            <button
              type="button"
              onClick={() => setShowItemImageModal(true)}
              className="w-full flex justify-center rounded-lg border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors min-h-[44px]"
              aria-label={t('warehouse.modules.inventory.status.showFullImage', {}, 'Vis varebilde i full størrelse')}
            >
              <img
                src={currentItemImageUrl}
                alt={currentItem.itemNo}
                loading="lazy"
                className="h-48 max-w-full object-contain bg-white"
                onError={() => setItemImageFailed(true)}
              />
            </button>
          </div>
        )}

        {/* Tellinger liste */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-3 sm:p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center text-sm sm:text-base">
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 text-green-500" />
                <span className="truncate">
                  {t('warehouse.modules.inventory.lists.counts', { '0': session.lines.length })}
                  {filterText && (
                    <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500">
                      - {filteredLines.length}
                    </span>
                  )}
                </span>
              </h2>
            </div>

            {/* Filter input */}
            <div className="relative">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('warehouse.modules.inventory.placeholders.filterItemsOrLocations')}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm min-h-[44px]"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {session.lines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Package className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventory.lists.noCountsYet')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventory.lists.scanToStart')}</p>
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Search className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventory.lists.noCountsMatchFilter')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventory.lists.tryDifferentSearch')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {filteredLines.map((line: InventoryCountLine) => (
                <div key={line.lineID} className="p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-1 sm:gap-0">
                        <div className="font-mono text-xs sm:text-sm font-semibold truncate">
                          {line.itemNo}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 truncate">
                          {line.description1}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{line.locationName}</span>
                        </span>

                        {/* Serial number badge */}
                        {line.itemSerialNo && (
                          <span className="flex items-center text-purple-600">
                            <Hash className="h-3 w-3 mr-0.5 flex-shrink-0" />
                            <span className="font-mono">{line.itemSerialNo}</span>
                            {line.quantity === 0 && (
                              <span className="ml-1 text-red-500 font-medium">{t('warehouse.modules.inventory.lines.missingTag', {}, '(mangler)')}</span>
                            )}
                          </span>
                        )}

                        {/* Batch number badge */}
                        {line.itemBatchNo && (
                          <span className="flex items-center text-orange-600">
                            <Package className="h-3 w-3 mr-0.5 flex-shrink-0" />
                            <span className="font-mono">{line.itemBatchNo}</span>
                            {line.quantity === 0 && (
                              <span className="ml-1 text-red-500 font-medium">{t('warehouse.modules.inventory.lines.missingTag', {}, '(mangler)')}</span>
                            )}
                          </span>
                        )}

                        {/* Quantity - editable or display */}
                        {editingLine && editingLine.lineID === line.lineID ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleSaveEdit();
                            }}
                            className="flex items-center space-x-1.5 sm:space-x-2"
                          >
                            <span>{t('warehouse.general.quantityShort')}:</span>
                            <NumericInput
                              ref={editQuantityInputRef}
                              mode="decimal"
                              value={editingLine.quantity}
                              onChange={(e) => setEditingLine(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="w-16 sm:w-20 px-2 py-1.5 border border-blue-300 rounded text-xs sm:text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[36px]"
                              min="0.01"
                            />
                            <div className="flex items-center space-x-1">
                              <button
                                type="submit"
                                disabled={updatingLineId === line.lineID}
                                className="p-1.5 text-green-600 hover:bg-green-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                                title={t('common.buttons.save')}
                              >
                                {updatingLineId === line.lineID ? (
                                  <Loader className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                                title={t('common.buttons.cancel')}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </form>
                        ) : (
                          <span>{t('warehouse.general.quantityShort')}: {line.quantity}</span>
                        )}

                        <span className="whitespace-nowrap">{new Date(line.transDt).toLocaleTimeString('no-NO')}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs whitespace-nowrap">
                        {line.statusText}
                      </span>

                      {/* Action buttons - kun vis hvis ikke i edit mode */}
                      {!editingLine && (
                        <div className="flex items-center space-x-1">
                          {/* Skjul redigering for serial/batch-linjer */}
                          {!line.itemSerialNo && !line.itemBatchNo && (
                          <button
                            onClick={() => handleStartEdit(line)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title={t('warehouse.modules.inventory.actions.editQuantity')}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          )}
                          <button
                            onClick={() => handleDeleteLine(line)}
                            disabled={deletingLineId === line.lineID}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title={t('warehouse.modules.inventory.actions.deleteCount')}
                          >
                            {deletingLineId === line.lineID ? (
                              <Loader className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ImageModal
        isOpen={showItemImageModal && !!currentItemImageUrl}
        imageUrl={currentItemImageUrl || ''}
        onClose={() => setShowItemImageModal(false)}
      />

      <ItemSearchModal
        isOpen={showItemSearchModal}
        onClose={() => setShowItemSearchModal(false)}
        onSelect={handleItemSearchSelect}
        title={t('warehouse.modules.inventory.modals.searchAndSelectItem')}
        warehouseId={session?.warehouse || currentWarehouse}
        skipLocationStep={locationOptional || itemsOnLocationMode}
        fetchLocations={async (itemNo: string) => {
          // Lokasjonene hentes fra tellingens lager — ikke lagervelgerens (kan være «Alle lager» eller et annet)
          const locations = await inventoryService.getLocationsForItem(itemNo, session?.warehouse || currentWarehouse);
          return locations.map((loc: ItemLocationInfo) => ({
            locationName: loc.locationName,
            warehouseID: loc.warehouseID || '',
            availableQuantity: loc.availableQuantity
          }));
        }}
      />

      <LocationSearchModal
        isOpen={showLocationSearchModal}
        onClose={() => setShowLocationSearchModal(false)}
        onSelect={handleLocationSearchSelect}
        title={t('warehouse.modules.inventory.modals.searchLocation')}
        warehouseID={session?.warehouse || undefined}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        enableRapidScanning={enableRapidScanning}
        onEnableRapidScanningChange={handleRapidScanningChange}
        showItemsOnLocationAvailable={!locationOptional}
        showItemsOnLocation={showItemsOnLocation}
        onShowItemsOnLocationChange={handleShowItemsOnLocationChange}
        saveError={settingsSaveError}
        onClose={() => { setShowSettingsModal(false); setSettingsSaveError(null); }}
      />

      {zeroDialog && (
        <LocationZeroCountDialog
          isOpen={true}
          locationName={zeroDialog.locationName}
          items={zeroDialog.items}
          onZero={handleZeroCountItem}
          onCount={handleZeroDialogCount}
          onFinish={handleZeroDialogFinish}
        />
      )}

      {/* Advarsel: antall over «Maks antall per linje» — samme vindu og tekster som tellelisten */}
      {highQuantityWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-orange-50 border-b border-orange-200 px-5 py-4 flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">{t('warehouse.modules.inventory.countList.unusuallyHighQuantity', {}, 'Uvanlig høyt antall')}</h3>
                <p className="text-xs text-orange-700 mt-0.5">{t('warehouse.modules.inventory.countList.maxRecommended', { '0': maxLineItemQuantity }, `Maks anbefalt: ${maxLineItemQuantity}`)}</p>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700">
                {t('warehouse.modules.inventory.countList.highQuantityIntro', {}, 'Du har tastet inn')} <span className="font-bold text-gray-900">{highQuantityWarning.quantity}</span>{t('warehouse.modules.inventory.countList.highQuantityExceeds', {}, ', som overstiger maks antall per linje (')}<span className="font-bold">{maxLineItemQuantity}</span>{t('warehouse.modules.inventory.countList.highQuantityExceedsEnd', {}, ').')}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {t('warehouse.modules.inventory.countList.confirmHighQuantity', {}, 'Er du sikker på at du vil registrere dette antallet?')}
              </p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t flex gap-3">
              <button
                onClick={handleHighQuantityCancel}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px]"
              >
                {t('warehouse.modules.inventory.countList.changeQuantity', {}, 'Nei, endre antall')}
              </button>
              <button
                onClick={handleHighQuantityConfirm}
                className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors min-h-[44px]"
              >
                {t('warehouse.modules.inventory.countList.registerAnyway', { '0': highQuantityWarning.quantity }, `Ja, registrer ${highQuantityWarning.quantity}`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {currentItem && currentLocation && (
        <SerialCountingModal
          isOpen={showSerialCountingModal}
          onClose={() => setShowSerialCountingModal(false)}
          onComplete={handleSerialCountComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={session?.warehouse || ''}
        />
      )}

      {currentItem && currentLocation && (
        <BatchCountingModal
          isOpen={showBatchCountingModal}
          onClose={() => setShowBatchCountingModal(false)}
          onComplete={handleBatchCountComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={session?.warehouse || ''}
          itemType={currentItem.itemType || 0}
        />
      )}

      {/* Non-intrusive capacity update button — appears briefly after count (AW only) */}
      {lastCountedInfo && !capacityModalData && (
        <div className="fixed bottom-20 right-4 z-40 animate-fade-in">
          <button
            onClick={async () => {
              try {
                const compNo = user?.companyNo || '';
                const bin = await stcBinService.getByExactBinCode(compNo, lastCountedInfo.binCode);
                if (bin) {
                  const config = await itemBinSizeService.getByCompanyItemAndBinSize(compNo, lastCountedInfo.itemNo, bin.binSizeNo);
                  setCapacityModalData({
                    itemNo: lastCountedInfo.itemNo,
                    itemDescription: lastCountedInfo.itemDescription,
                    binCode: lastCountedInfo.binCode,
                    binSizeNo: bin.binSizeNo,
                    currentQuantity: lastCountedInfo.quantity,
                    currentMaxQuantity: config?.maxQuantity,
                    existingItemBinSizeId: config?.id ?? null,
                    existingPutAwayPriorityNo: config?.putAwayPriorityNo ?? bin.putAwayPriorityNo ?? 1,
                  });
                  setLastCountedInfo(null);
                }
              } catch { /* Non-critical */ }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-[#0E7C86]/30 rounded-lg shadow-lg text-xs font-medium text-[#0E7C86] dark:text-[#4fd1c5] hover:bg-[#0E7C86]/5 transition-colors"
          >
            <Layers className="w-3.5 h-3.5" />
            {t('warehouse.modules.inventory.capacity.updateButton', { '0': lastCountedInfo.itemNo }, `Oppdater kapasitet — ${lastCountedInfo.itemNo}`)}
          </button>
        </div>
      )}

      {/* Capacity Update Modal */}
      {capacityModalData && (
        <CapacityUpdateModal
          isOpen={true}
          onClose={() => setCapacityModalData(null)}
          onSaved={() => setCapacityModalData(null)}
          itemNo={capacityModalData.itemNo}
          itemDescription={capacityModalData.itemDescription}
          binCode={capacityModalData.binCode}
          binSizeNo={capacityModalData.binSizeNo}
          currentQuantity={capacityModalData.currentQuantity}
          currentMaxQuantity={capacityModalData.currentMaxQuantity}
          existingItemBinSizeId={capacityModalData.existingItemBinSizeId}
          existingPutAwayPriorityNo={capacityModalData.existingPutAwayPriorityNo}
        />
      )}
    </div>
  );
};